import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { api } from "../services/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { thaiUser } from "../utils/thaiUser";
import i18n from "../i18n";

interface User {
  _id: string;
  email: string;
  username: string;
  mobileNo?: string;
  dob?: string;
  roleId: number;
  roleIds?: number[];
  activeRoleId?: number;
  categories?: any[];
  subscription?: any;
  isAdmin?: boolean;
  subscriptionId?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  subscriptionStatus?: string;
  preferredLanguage?: string;
  region?: string;
  // Regions purchased via Journey — separate from `region`, which is the one
  // region whose content is currently active.
  unlockedRegions?: string[];
  allRegionsApproved?: boolean;
  allRegionsPending?: boolean;
  gccToneMode?: "msa_fusha" | "gulf" | "kuwaiti";
  // spanish tones lock
  spanishToneLock?:
    | "neutral"
    | "spain"
    | "mexico"
    | "argentina"
    | "colombia"
    | "chile"
    | "peru"
    | null;
  hasCompatibilityProfile?: boolean;
}

interface LoginResponse {
  message: string;
  token: string;
  migratedChats: string[];
  user: {
    _id: string;
    email: string;
    username: string;
    roleId: number;
    subscriptionId?: string;
    subscriptionStartDate?: string;
    subscriptionEndDate?: string;
    subscriptionStatus?: string;
    preferredLanguage?: string;
  };
}

interface RegisterResponse {
  message: string;
  userId: string;
  roleId: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    anonymousChatIds?: string[],
  ) => Promise<LoginResponse>;
  register: (
    username: string,
    email: string,
    password: string,
    mobileNo?: string,
    preferredLanguage?: string,
    roleId?: string,
    region?: string,
  ) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  appleLogin: (code: string) => Promise<void>;
  logout: () => void;
  updateCurrentUser: (updates: Partial<User>) => void;
  sendOtp: (email: string) => Promise<any>;
  verifyOtp: (email: string, otp: string) => Promise<any>;
  resetPassword: (resetToken: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem("user");
    return saved ? thaiUser(JSON.parse(saved)) : null;
  });
  const [token, setToken] = useState<string | null>(
    localStorage.getItem("token"),
  );
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token"),
  );
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Thai-only build (thai-production): the UI language is always Thai and the
  // user's stored preference is ignored.
  const applyPreferredLanguage = (_preferredLanguage?: string) => {
    try {
      localStorage.setItem("preferredLanguage", "th");
    } catch {
      // ignore
    }

    if (i18n.language !== "th") {
      i18n.changeLanguage("th").catch(() => {
        // ignore
      });
    }
  };

  // Initialize auth state
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(thaiUser(parsedUser));
        setToken(savedToken);
        setIsAuthenticated(true);
        applyPreferredLanguage(parsedUser?.preferredLanguage);
        // Set default authorization header
        api.defaults.headers.common["Authorization"] = `Bearer ${savedToken}`;
      } catch (error) {
        console.error("Failed to parse saved auth data:", error);
        logout();
      }
    }
  }, []);

  // Login function matching your backend EXACTLY
  const login = async (
    email: string,
    password: string,
    anonymousChatIds: string[] = [],
  ): Promise<LoginResponse> => {
    try {
      setIsLoading(true);

      const response = await api.post<LoginResponse>("/login", {
        email,
        password,
        anonymousChatIds, // Send anonymous chat IDs
      });

      if (response.data.message === "Login successful") {
        const { token, user, migratedChats } = response.data;

        // Save token and user info
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(thaiUser(user)));
        localStorage.setItem("userId", user._id);
        localStorage.setItem("subscriptionId", user.subscriptionId || "");
        localStorage.setItem("userRegion", (user as any).region || "healjai");
        applyPreferredLanguage(user?.preferredLanguage);

        // Clear anonymous chats from localStorage after successful migration
        if (migratedChats && migratedChats.length > 0) {
          localStorage.removeItem("anonymous_chats");
          // toast.success(
          //   `${migratedChats.length} chat(s) migrated to your account`,
          // );
        } else if (anonymousChatIds.length > 0) {
          console.log(
            "No chats were migrated, but anonymous chats still exist in localStorage",
          );
        }

        setUser(thaiUser(user));
        setToken(token);
        setIsAuthenticated(true);
        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        toast.success("เข้าสู่ระบบสำเร็จ!");

        // Return the response data
        return response.data;
      } else {
        throw new Error("เข้าสู่ระบบไม่สำเร็จ");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.error || "เข้าสู่ระบบไม่สำเร็จ");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (idToken: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post<LoginResponse>("/googleLogin", {
        idToken,
      });

      if (response.data.message === "Google login successful") {
        const { token, user: userData } = response.data;

        const formattedUser: User = {
          _id: userData._id,
          email: userData.email,
          username: userData.username,
          roleId: userData.roleId,
          categories: [],
          subscription: null,
          subscriptionId: userData.subscriptionId,
          subscriptionStartDate: userData.subscriptionStartDate,
          subscriptionEndDate: userData.subscriptionEndDate,
          subscriptionStatus: userData.subscriptionStatus,
          preferredLanguage: (userData as any)?.preferredLanguage,
          region: (userData as any)?.region || "healjai",
          unlockedRegions: (userData as any)?.unlockedRegions || [],
          allRegionsApproved: (userData as any)?.allRegionsApproved || false,
          allRegionsPending: (userData as any)?.allRegionsPending || false,
          hasCompatibilityProfile:
            (userData as any)?.hasCompatibilityProfile || false,
        };

        setUser(thaiUser(formattedUser));
        setToken(token);
        setIsAuthenticated(true);

        localStorage.setItem("user", JSON.stringify(thaiUser(formattedUser)));
        localStorage.setItem("token", token);
        localStorage.setItem("userId", formattedUser._id);
        localStorage.setItem(
          "subscriptionId",
          formattedUser.subscriptionId || "",
        );
        localStorage.setItem("userRegion", formattedUser.region || "healjai");

        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        applyPreferredLanguage(formattedUser?.preferredLanguage);

        toast.success("เข้าสู่ระบบด้วย Google สำเร็จ!");
      } else {
        throw new Error(response.data.message || "เข้าสู่ระบบด้วย Google ไม่สำเร็จ");
      }
    } catch (error: any) {
      const msg =
        error.response?.data?.error || error.message || "เข้าสู่ระบบด้วย Google ไม่สำเร็จ";
      toast.error(msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const appleLogin = async (code: string): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post<LoginResponse>("/appleLogin", { code });

      if (response.data.message === "Apple login successful") {
        const { token, user: userData } = response.data;

        const formattedUser: User = {
          _id: userData._id,
          email: userData.email,
          username: userData.username,
          roleId: userData.roleId,
          categories: [],
          subscription: null,
          subscriptionId: userData.subscriptionId,
          subscriptionStartDate: userData.subscriptionStartDate,
          subscriptionEndDate: userData.subscriptionEndDate,
          subscriptionStatus: userData.subscriptionStatus,
          preferredLanguage: (userData as any)?.preferredLanguage,
          region: (userData as any)?.region || "healjai",
          unlockedRegions: (userData as any)?.unlockedRegions || [],
          allRegionsApproved: (userData as any)?.allRegionsApproved || false,
          allRegionsPending: (userData as any)?.allRegionsPending || false,
          hasCompatibilityProfile:
            (userData as any)?.hasCompatibilityProfile || false,
        };

        setUser(thaiUser(formattedUser));
        setToken(token);
        setIsAuthenticated(true);

        localStorage.setItem("user", JSON.stringify(thaiUser(formattedUser)));
        localStorage.setItem("token", token);
        localStorage.setItem("userId", formattedUser._id);
        localStorage.setItem(
          "subscriptionId",
          formattedUser.subscriptionId || "",
        );
        localStorage.setItem("userRegion", formattedUser.region || "healjai");

        api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        applyPreferredLanguage(formattedUser?.preferredLanguage);

        toast.success("เข้าสู่ระบบด้วย Apple สำเร็จ!");
      } else {
        throw new Error(response.data.message || "เข้าสู่ระบบด้วย Apple ไม่สำเร็จ");
      }
    } catch (error: any) {
      const msg =
        error.response?.data?.error || error.message || "เข้าสู่ระบบด้วย Apple ไม่สำเร็จ";
      toast.error(msg);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register function matching your backend EXACTLY
  const register = async (
    username: string,
    email: string,
    password: string,
    mobileNo?: string,
    preferredLanguage?: string,
    roleId: string = "2",
    region: string = "healjai",
  ): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await api.post<RegisterResponse>("/register", {
        username: username.trim(),
        email: email.trim(),
        password: password.trim(),
        mobileNo: mobileNo?.trim(),
        preferredLanguage: preferredLanguage?.trim(),
        roleId: parseInt(roleId),
        region: region || "healjai",
      });

      console.log("Register response:", response.data);

      if (response.data.message === "User registered successfully") {
        // Auto-login after successful registration
        await login(email, password);
        toast.success("สมัครสมาชิกสำเร็จ!");
      } else {
        //@ts-ignore
        throw new Error(response.data.error || "สมัครสมาชิกไม่สำเร็จ");
      }
    } catch (error: any) {
      console.error("Registration error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });

      let errorMessage = "สมัครสมาชิกไม่สำเร็จ";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // OTP functions
  const sendOtp = async (email: string) => {
    try {
      const response = await api.post("/sendOtp", { email });
      return response.data;
    } catch (error: any) {
      console.error("Send OTP error:", error);
      throw error;
    }
  };

  const verifyOtp = async (email: string, otp: string) => {
    try {
      const response = await api.post("/verifyOtp", { email, otp });
      return response.data;
    } catch (error: any) {
      console.error("Verify OTP error:", error);
      throw error;
    }
  };

  const resetPassword = async (resetToken: string, newPassword: string) => {
    try {
      const response = await api.put("/resetPasswordWithOtp", {
        resetToken,
        newPassword,
      });

      if (response.data.message === "Password reset successful") {
        toast.success("ตั้งรหัสผ่านใหม่สำเร็จ!");
      } else {
        throw new Error(response.data.error || "ตั้งรหัสผ่านใหม่ไม่สำเร็จ");
      }
    } catch (error: any) {
      console.error("Reset password error:", error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("userRegion");
    delete api.defaults.headers.common["Authorization"];
    navigate("/");
    toast.success("ออกจากระบบสำเร็จ");
  };

  const updateCurrentUser = (updates: Partial<User>) => {
    setUser((prev) => {
      let base: any = prev;
      if (!base) {
        try {
          base = JSON.parse(localStorage.getItem("user") || "null");
        } catch (e) {
          base = null;
        }
      }

      const merged = thaiUser({ ...(base || {}), ...updates } as User);
      localStorage.setItem("user", JSON.stringify(merged));
      if (updates?.preferredLanguage) {
        applyPreferredLanguage(merged.preferredLanguage);
      }
      if (updates?.region) {
        localStorage.setItem("userRegion", merged.region || "healjai");
      }
      return merged;
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        isLoading,
        login,
        googleLogin,
        appleLogin,
        register,
        logout,
        updateCurrentUser,
        sendOtp,
        verifyOtp,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
